import {
  Button,
  Card,
  Group,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { showNotification } from "@mantine/notifications";
import {
  IconCirclePlus,
  IconDots,
  IconLayoutGrid,
  IconPackage,
  IconPlus,
  IconPointFilled,
  IconSpacingVertical,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { MantineReactTable } from "mantine-react-table";
import * as React from "react";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import {
  createConfigGroupProduct,
  createConfigGroupUnified,
  deleteConfigGroupProduct,
  editConfigGroupUnified,
} from "../../../../api/ApiConfigGroup";
import {
  TblConfigGroupAttribute,
  TblConfigGroupProduct,
} from "../../../../model/TblConfigGroup";
import FormChooseItem from "../../ConfigGroup/EditView/FormChooseItem";

interface VariantOption {
  id: string;
  dbId?: number;
  value: string;
  colorCode?: string;
  image?: string;
}

interface VariantGroup {
  id: string;
  dbId?: number;
  name: string;
  options: VariantOption[];
}

interface SKUVariant {
  id: string;
  combination: Record<string, string>;
  price: string;
  stock: string;
  sku: string;
  gtin: string;
  noGtin: boolean;
  image?: string;
  productId?: number | null;
  productName?: string | null;
  productImage?: string | null;
  productCode?: string | null;
  productMarketPrice?: string | null;
  url?: string | null;
}

interface productOptionAttribute {
  id: number;
  attributeId: number | null;
  attributeValueId: number | null;
  isChange: boolean;
}

interface productOptionType {
  productId: number | null;
  itemName: string | null;
  attributes: productOptionAttribute[];
}

const ConfigGroupProduct = ({
  idGroup,
  initialName = "",
  initialDescription = "",
  dataConfigGroupProduct,
  dataConfigGroupAttribute,
  callApiGetData,
  onSaved,
}: ConfigGroupProductProps) => {
  const [hasVariants, setHasVariants] = React.useState(true);
  const [groups, setGroups] = React.useState<VariantGroup[]>([
    { id: "1", name: "", options: [{ id: "1-1", value: "" }] },
  ]);
  const [skus, setSkus] = React.useState<SKUVariant[]>([]);

  const [groupName, setGroupName] = React.useState(initialName);
  const [groupDescription, setGroupDescription] =
    React.useState(initialDescription);
  const normalizeInput = (str: string) =>
    str
      .replace(/\s{2,}/g, " ") // collapse multiple spaces to one
      .replace(/^\s+/, "") // remove leading spaces
      .replace(/\s+$/, ""); // remove trailing spaces
  const preventLeadingSpaceOnKey = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.key === " ") {
      const t = e.currentTarget as HTMLInputElement | HTMLTextAreaElement;
      if ((t.selectionStart ?? 0) === 0 && (t.selectionEnd ?? 0) === 0 && !t.value) {
        e.preventDefault();
      }
    }
  };

  const makePasteHandler = (setter: (v: string) => void) => (
    e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const raw = e.clipboardData.getData("text");
    const trimmedStart = raw.replace(/^\s+/, "");
    if (trimmedStart !== raw) {
      e.preventDefault();
      const t = e.currentTarget as HTMLInputElement | HTMLTextAreaElement;
      const start = t.selectionStart ?? 0;
      const end = t.selectionEnd ?? 0;
      const newVal = t.value.slice(0, start) + trimmedStart + t.value.slice(end);
      setter(newVal);
    }
  };
  const [isSaving, setIsSaving] = React.useState(false);
  const prevAttrKeyRef = React.useRef<string>("");

  const [bulkPrice, setBulkPrice] = React.useState("");
  const [bulkStock, setBulkStock] = React.useState("");
  const [bulkPrefix, setBulkPrefix] = React.useState("");

  const [dataConfigGroupProductOption, setDataConfigGroupProductOption] =
    React.useState<productOptionType[]>([]);

  const entity = {
    id: 0,
    productId: null,
    itemName: null,
    groupId: idGroup,
    attributeValueId: null,
    attributeId: null,
    attributeConfig: null,
  };

  const handleChooseItem = async (idItem: number, itemName: string | null) => {
    const dataItem = { ...entity, productId: idItem, itemName: itemName };

    try {
      await createConfigGroupProduct(dataItem);
      showNotification({
        title: "Thành công",
        message: "Đã thêm sản phẩm vào nhóm",
      });
      callApiGetData && callApiGetData();
    } catch (e) {
      NotificationExtension.Fails("Thêm sản phẩm thất bại");
    }
  };

  React.useEffect(() => {
    if (!hasVariants) {
      setSkus([]);
      return;
    }

    setSkus((prevSkus) => {
      const generateCombinations = (
        index: number,
        current: Record<string, string>,
      ): Record<string, string>[] => {
        if (index === groups.length) return [current];

        const group = groups[index];
        const groupKey = (group.name || "").trim() || `__g${index}`;
        // If group has no options or all options are empty, skip this group
        const hasAnyOptionValue = group.options.some((o) => !!o.value);
        if (group.options.length === 0 || !hasAnyOptionValue)
          return generateCombinations(index + 1, current);

        const results: Record<string, string>[] = [];
        group.options.forEach((opt) => {
          if (opt.value) {
            results.push(
              ...generateCombinations(index + 1, {
                ...current,
                [groupKey]: opt.value,
              }),
            );
          }
        });
        return results;
      };

      const combinations = generateCombinations(0, {});

      // Preserve previous SKU assignments if possible.
      // Matching priority:
      // 1) exact combination string match (and not reused)
      // 2) same productId (previously assigned) and not reused
      const usedPrevSkuIds = new Set<string>();
      const newSkus = combinations.map((combo) => {
        const comboKey = Object.values(combo).join("-");

        let existing = prevSkus.find(
          (s) =>
            Object.values(s.combination).join("-") === comboKey &&
            !usedPrevSkuIds.has(s.id),
        );

        if (!existing) {
          existing = prevSkus.find(
            (s) => s.productId != null && !usedPrevSkuIds.has(s.id),
          );
        }

        if (existing) {
          usedPrevSkuIds.add(existing.id);
          // ensure combination keys are updated to current group names
          return { ...existing, combination: combo };
        }

        return {
          id: Math.random().toString(36).slice(2, 11),
          combination: combo,
          price: "",
          stock: "",
          sku: "",
          gtin: "",
          noGtin: false,
          productId: null,
          productName: null,
          productImage: "",
          productCode: null,
          productMarketPrice: null,
        };
      });

      return newSkus;
    });
  }, [groups, hasVariants]);

  // Sync name/description khi props thay đổi (sau callApiGetData)
  React.useEffect(() => {
    setGroupName(initialName ?? "");
  }, [initialName]);

  React.useEffect(() => {
    setGroupDescription(initialDescription ?? "");
  }, [initialDescription]);

  // Khởi tạo groups và skus từ dữ liệu DB khi load hoặc sau khi lưu
  React.useEffect(() => {
    if (!dataConfigGroupAttribute?.length) return;

    const attrKey = (dataConfigGroupAttribute as TblConfigGroupAttribute[])
      .map((a) => a.id)
      .join(",");
    if (attrKey === prevAttrKeyRef.current) return;
    prevAttrKeyRef.current = attrKey;

    // 1. Build groups with dbId từ DB
    const initGroups: VariantGroup[] = (
      dataConfigGroupAttribute as TblConfigGroupAttribute[]
    ).map((attr) => ({
      id: String(attr.id),
      dbId: attr.id,
      name: attr.name || "",
      options: (attr.tblConfigGroupAttributeValueModels || []).map((val) => ({
        id: String(val.id),
        dbId: val.id,
        value: val.name || "",
        colorCode: val.colorCode ?? undefined,
        image: val.image ?? undefined,
      })),
    }));

    // 2. Bảng tra cứu: attributeValueId → { groupName, optionValue }
    const valMap = new Map<
      number,
      { groupName: string; optionValue: string }
    >();
    initGroups.forEach((g) => {
      g.options.forEach((o) => {
        if (o.dbId)
          valMap.set(o.dbId, { groupName: g.name, optionValue: o.value });
      });
    });

    // 3. Build skus từ products hiện có
    const productMap = new Map<
      number,
      {
        productId: number;
        itemName: string | null;
        valIds: number[];
        item?: any;
      }
    >();
    ((dataConfigGroupProduct as TblConfigGroupProduct[]) || []).forEach(
      (p: any) => {
        if (!p.productId) return;
        if (!productMap.has(p.productId)) {
          productMap.set(p.productId, {
            productId: p.productId,
            itemName: p.itemName,
            valIds: [],
            item: p.item,
          });
        }

        const entry = productMap.get(p.productId)!;
        // Read unified API `variants` array
        if (Array.isArray(p.variants)) {
          p.variants.forEach((v: any) => {
            if (
              v.attributeValueId != null &&
              !entry.valIds.includes(v.attributeValueId)
            ) {
              entry.valIds.push(v.attributeValueId);
            }
          });
        }
        // Read legacy flat root struct if present
        if (
          p.attributeValueId != null &&
          !entry.valIds.includes(p.attributeValueId)
        ) {
          entry.valIds.push(p.attributeValueId);
        }
      },
    );

    const generateCombinations = (
      index: number,
      current: Record<string, string>,
    ): Record<string, string>[] => {
      if (index === initGroups.length) return [current];

      const group = initGroups[index];
      const groupKey = (group.name || "").trim() || `__g${index}`;
      const hasAnyOptionValue = group.options.some((o) => !!o.value);
      if (group.options.length === 0 || !hasAnyOptionValue)
        return generateCombinations(index + 1, current);

      const results: Record<string, string>[] = [];
      group.options.forEach((opt) => {
        if (opt.value) {
          results.push(
            ...generateCombinations(index + 1, {
              ...current,
              [groupKey]: opt.value,
            }),
          );
        }
      });
      return results;
    };

    const validCombos = generateCombinations(0, {});
    const productsList = Array.from(productMap.values());

    const initSkus: SKUVariant[] = productsList.map((prod, index) => {
      let combination: Record<string, string> = {};
      let isMatchFromDB = false;

      prod.valIds.forEach((valId) => {
        const info = valMap.get(valId);
        if (info) {
          combination[info.groupName] = info.optionValue;
          isMatchFromDB = true;
        }
      });

      // Nếu variant attribute ID trong DB không khớp (bị xóa rồi sinh id mới),
      // fallback gắn product theo vị trí tổ hợp ngẫu nhiên.
      if (!isMatchFromDB && validCombos[index]) {
        combination = validCombos[index];
      }

      return {
        id: String(prod.productId),
        combination,
        price:
          prod.item?.unitSellingPrice != null
            ? String(prod.item.unitSellingPrice)
            : "",
        stock: "",
        sku: prod.item?.sku || prod.item?.itemCode || "",
        gtin: "",
        noGtin: false,
        productId: prod.productId,
        productName: prod.itemName || prod.item?.itemName || null,
        productImage: prod.item?.primaryImage || "",
        productCode: prod.item?.itemCode || prod.item?.sku || null,
        productMarketPrice:
          prod.item?.marketPrice != null ? String(prod.item.marketPrice) : null,
        url: prod.item?.url || null,
      };
    });

    // Set skus trước để useEffect groups→skus preserve product assignments
    if (initSkus.length > 0) setSkus(initSkus);
    setGroups(
      initGroups.length > 0
        ? initGroups
        : [{ id: "1", name: "", options: [{ id: "1-1", value: "" }] }],
    );
  }, [dataConfigGroupAttribute, dataConfigGroupProduct]);

  // Handler gửi Unified API (Create hoặc Edit)
  const handleSaveAndPublish = React.useCallback(async () => {
    if (!groupName.trim()) {
      NotificationExtension.Fails("Vui lòng nhập tên nhóm");
      return;
    }
    setIsSaving(true);
    try {
      let tempGroupSeq = 1;
      let tempValueSeq = 1;
      const tempGroupIdMap = new Map<string, number>();
      const tempValueIdMap = new Map<string, number>();

      const attributes = groups.map((group, gIdx) => {
        const attrId = group.dbId ?? tempGroupSeq++;
        tempGroupIdMap.set(group.id, attrId);

        return {
          id: attrId,
          name: group.name,
          orderNumber: gIdx + 1,
          values: group.options
            .filter((o) => o.value.trim())
            .map((opt, oIdx) => {
              const mapKey = `${group.id}:${opt.id}`;
              const valId = opt.dbId ?? tempValueSeq++;
              if (!tempValueIdMap.has(mapKey)) {
                tempValueIdMap.set(mapKey, valId);
              }

              return {
                id: tempValueIdMap.get(mapKey),
                name: opt.value,
                colorCode: opt.colorCode ?? null,
                image: opt.image ?? null,
                ordering: oIdx + 1,
              };
            }),
        };
      });

      // Build products payload directly from current visible SKUs.
      // This strictly clears out any zombie products / dropped combos.
      const finalProducts = skus
        .filter((sku) => sku.productId != null)
        .map((sku) => {
          const pid = sku.productId as number;
          const variants = Object.entries(sku.combination)
            .map(([gName, optVal]) => {
              const groupIndex = groups.findIndex(
                (g) => (g.name || "").trim() === gName,
              );
              if (groupIndex < 0) return null;
              const grp = groups[groupIndex];
              const optionIndex = grp.options.findIndex(
                (o) => o.value === optVal,
              );
              if (optionIndex < 0) return null;
              const opt = grp.options[optionIndex];
              const mapKey = `${grp.id}:${opt.id}`;

              const valId = tempValueIdMap.get(mapKey);
              const attrId = tempGroupIdMap.get(grp.id);

              return {
                attributeId: attrId ?? grp.dbId ?? groupIndex + 1,
                attributeValueId: valId ?? opt.dbId ?? optionIndex + 1,
              } as { attributeId: number; attributeValueId: number };
            })
            .filter(
              (v): v is { attributeId: number; attributeValueId: number } =>
                v !== null,
            );

          return {
            productId: pid,
            itemName:
              sku.productName ||
              Object.values(sku.combination).join(" - ") ||
              "",
            variants,
          };
        });

      // Debug payload
      try {
        // eslint-disable-next-line no-console
        console.debug("ConfigGroupProduct:finalPayload", {
          attributes,
          products: finalProducts,
        });
      } catch (e) {}

      const normalizeString = (str: string) => {
        if (!str) return "";
        return str
          .toLowerCase()
          .trim()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[đĐ]/g, "d");
      };

      // Check duplicate group names
      const normalizedGroupNames = groups
        .map((g) => g.name.trim())
        .filter((name) => name !== "")
        .map(normalizeString);

      const hasDuplicateGroupName = normalizedGroupNames.some(
        (name, index) => normalizedGroupNames.indexOf(name) !== index,
      );
      if (hasDuplicateGroupName) {
        NotificationExtension.Fails(
          "Tên phân loại không được trùng nhau (không phân biệt hoa thường, dấu)",
        );
        return;
      }

      // Check duplicate options in each group
      for (const group of groups) {
        const normalizedOptions = group.options
          .map((o) => o.value.trim())
          .filter((val) => val !== "")
          .map(normalizeString);

        const hasDuplicateOption = normalizedOptions.some(
          (val, index) => normalizedOptions.indexOf(val) !== index,
        );
        if (hasDuplicateOption) {
          NotificationExtension.Fails(
            `Tên tùy chọn trong phân loại "${group.name}" không được trùng nhau (không phân biệt hoa thường, dấu)`,
          );
          return;
        }
      }

      // Check duplicate products
      const productIds = skus
        .filter((sku) => sku.productId != null)
        .map((sku) => sku.productId);

      const duplicateProductId = productIds.find(
        (id, index) => productIds.indexOf(id) !== index,
      );
      if (duplicateProductId) {
        const duplicateProduct = skus.find(
          (s) => s.productId === duplicateProductId,
        );
        NotificationExtension.Fails(
          `Sản phẩm "${duplicateProduct?.productName}" đã được chọn nhiều lần. Mỗi sản phẩm chỉ được chọn một lần duy nhất.`,
        );
        return;
      }

      if (idGroup) {
        await editConfigGroupUnified({
          id: idGroup,
          name: groupName,
          description: groupDescription,
          configGroupType: "",
          attributes,
          products: finalProducts,
        });
        callApiGetData && (await callApiGetData());
      } else {
        await createConfigGroupUnified([
          {
            name: groupName,
            description: groupDescription,
            configGroupType: "",
            attributes,
            products: finalProducts,
          },
        ]);
        // onSaved && (await onSaved());
      }
    } catch {
      NotificationExtension.Fails("Lưu thất bại, vui lòng thử lại");
    } finally {
      setIsSaving(false);
    }
  }, [
    groupName,
    groupDescription,
    groups,
    skus,
    idGroup,
    callApiGetData,
    onSaved,
  ]);

  const addGroup = React.useCallback(() => {
    setGroups((prev) =>
      prev.length < 2
        ? [
            ...prev,
            {
              id: Date.now().toString(),
              name: "",
              options: [{ id: Date.now().toString() + "-1", value: "" }],
            },
          ]
        : prev,
    );
  }, []);

  const removeGroup = React.useCallback((id: string) => {
    setGroups((prev) => prev.filter((g) => g.id !== id));
  }, []);

  const addOption = React.useCallback((groupId: string) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? {
              ...g,
              options: [...g.options, { id: Date.now().toString(), value: "" }],
            }
          : g,
      ),
    );
  }, []);

  const updateOption = React.useCallback(
    (groupId: string, optionId: string, value: string) => {
      setGroups((prev) =>
        prev.map((g) =>
          g.id === groupId
            ? {
                ...g,
                options: g.options.map((o) =>
                  o.id === optionId ? { ...o, value } : o,
                ),
              }
            : g,
        ),
      );
    },
    [],
  );

  const removeOption = React.useCallback(
    (groupId: string, optionId: string) => {
      setGroups((prev) =>
        prev.map((g) =>
          g.id === groupId
            ? { ...g, options: g.options.filter((o) => o.id !== optionId) }
            : g,
        ),
      );
    },
    [],
  );

  const applyBulk = React.useCallback(() => {
    setSkus((prev) =>
      prev.map((s) => ({
        ...s,
        price: bulkPrice || s.price,
        stock: bulkStock || s.stock,
        sku: bulkPrefix
          ? `${bulkPrefix}-${Object.values(s.combination).join("-")}`
          : s.sku,
      })),
    );
    showNotification({
      title: "Đã áp dụng",
      message: "Cập nhật thành công cho tất cả phân loại",
    });
  }, [bulkPrice, bulkStock, bulkPrefix]);

  // Build grouped product options from incoming props (like previous version)
  React.useEffect(() => {
    const groupedOptions: productOptionType[] = (
      (dataConfigGroupProduct || []) as TblConfigGroupProduct[]
    ).reduce((acc: productOptionType[], product: TblConfigGroupProduct) => {
      const existingProductIndex = acc.findIndex(
        (item) => item.productId === product.productId,
      );
      if (existingProductIndex !== -1) {
        acc[existingProductIndex].attributes.push({
          id: product.id!,
          attributeId: product.attributeId,
          attributeValueId: product.attributeValueId,
          isChange: false,
        });
      } else {
        acc.push({
          productId: product.productId!,
          itemName: product.itemName,
          attributes: [
            {
              id: product.id!,
              attributeId: product.attributeId,
              attributeValueId: product.attributeValueId,
              isChange: false,
            },
          ],
        });
      }
      return acc;
    }, []);

    setDataConfigGroupProductOption(groupedOptions);
  }, [dataConfigGroupProduct]);

  const deleteSku = React.useCallback((skuId: string) => {
    setSkus((prev) => prev.filter((s) => s.id !== skuId));
  }, []);

  const updateSkuField = React.useCallback(
    (
      skuId: string,
      field: keyof Omit<SKUVariant, "id" | "combination">,
      value: string | boolean,
    ) => {
      setSkus((prev) =>
        prev.map((s) => (s.id === skuId ? { ...s, [field]: value } : s)),
      );
    },
    [],
  );

  const handleOpenFormChooseItemForRow = React.useCallback(
    (skuId: string) => {
      modals.openConfirmModal({
        modalId: "item",
        title: (
          <>
            <div color="white">
              <Title order={5}>Chọn sản phẩm cho phân loại</Title>
            </div>
          </>
        ),
        children: (
          <FormChooseItem
            onChooseItem={(
              idItem: number,
              itemName: string | null,
              itemCode?: string | null,
              sku?: string | null,
              primaryImage?: string | null,
              unitSellingPrice?: number | null,
              marketPrice?: number | null,
              url?: string | null,
            ) => {
              setSkus((prev) =>
                prev.map((s) =>
                  s.id === skuId
                    ? {
                        ...s,
                        productId: idItem,
                        productName: itemName,
                        productImage: primaryImage || "",
                        productCode: itemCode || sku || null,
                        productMarketPrice: marketPrice
                          ? String(marketPrice)
                          : null,
                        sku: itemCode || sku || s.sku,
                        url: url,
                        price: unitSellingPrice
                          ? String(unitSellingPrice)
                          : s.price,
                      }
                    : s,
                ),
              );
            }}
          />
        ),
        confirmProps: { display: "none" },
        cancelProps: { display: "none" },
      });
    },
    [updateSkuField],
  );

  const tableDataWithSpan = React.useMemo(() => {
    const keys = groups.map((g, i) => (g.name || "").trim() || `__g${i}`);
    const base = skus.map((s) => ({
      ...s,
      group1: s.combination[keys[0] || ""] || "",
      group2: s.combination[keys[1] || ""] || "",
    }));

    // compute rowSpan for group1 (and group2 if needed)
    type RowWithSpan = (typeof base)[number] & { _rowSpanGroup1?: number };
    const result: RowWithSpan[] = base.map((row) => ({ ...row }));
    // group1
    for (let i = 0; i < result.length; ) {
      const val = result[i].group1;
      if (!val) {
        result[i]._rowSpanGroup1 = 1;
        i++;
        continue;
      }
      let j = i + 1;
      while (j < result.length && result[j].group1 === val) j++;
      const span = j - i;
      result[i]._rowSpanGroup1 = span;
      for (let k = i + 1; k < j; k++) {
        result[k]._rowSpanGroup1 = 0; //
      }
      i = j;
    }

    return result;
  }, [skus, groups]);

  const columns = React.useMemo(() => {
    const cols: any[] = [];
    const key0 = (groups[0]?.name || "").trim();
    if (key0)
      cols.push({
        accessorKey: "group1",
        header: (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyItems: "center",
              gap: 8,
            }}
          >
            <p
              style={{
                color: "red",
                fontWeight: "bold",
                fontSize: 18,
                display: "flex",
              }}
            >
              <IconPointFilled size={20} />
            </p>
            <span>{groups[0].name}</span>
          </div>
        ),
        size: 100,
        mantineTableBodyCellProps: ({ row }: any) => {
          const span = row.original._rowSpanGroup1 ?? 1;
          if (span === 0) return { style: { display: "none" } };
          return { rowSpan: span, style: { verticalAlign: "middle" } } as any;
        },
        Cell: ({ row }: any) => {
          const val = row.original.group1;
          if (!val) return null;
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                paddingLeft: 15,
              }}
            >
              {val}
            </div>
          );
        },
      });
    const key1 = (groups[1]?.name || "").trim();
    if (key1)
      cols.push({
        accessorKey: "group2",
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <p
              style={{
                color: "red",
                fontWeight: "bold",
                fontSize: 18,
                display: "flex",
              }}
            >
              <IconPointFilled size={20} />
            </p>
            <span>{groups[1].name}</span>
          </div>
        ),
        size: 100,
        Cell: ({ row, table }: any) => {
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {row.original.group2}
            </div>
          );
        },
      });

    cols.push({
      accessorKey: "product",
      header: "Sản phẩm",
      size: 300,
      Cell: ({ row }: any) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            textAlign: "start",
            gap: 12,
            padding: "10px 0",
          }}
        >
          {row.original.productImage ? (
            <img
              src={row.original.productImage}
              alt={row.original.productName || ""}
              style={{
                width: 40,
                height: 40,
                objectFit: "cover",
                borderRadius: 6,
                border: "1px solid rgba(0, 0, 0, 0.25)",
              }}
            />
          ) : (
            <div
              style={{
                width: 40,
                height: 40,
                background: "#c2d5ffff",
                borderRadius: 6,
              }}
            />
          )}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "start",
              alignItems: "flex-start",
            }}
          >
            {row.original.productName ? (
              <p
                style={{
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "start",
                  justifyItems: "start",
                }}
              >
                {row.original.productName}
              </p>
            ) : (
              <div
                style={{
                  width: 160,
                  height: 14,
                  background: "#c2d5ffff",
                  borderRadius: 4,
                  marginBottom: 6,
                }}
              />
            )}

            {row.original.productCode ? (
              <span style={{ color: "#6b7280", fontSize: 12 }}>
                {row.original.productCode}
              </span>
            ) : (
              <div
                style={{
                  width: 100,
                  height: 12,
                  background: "#c2d5ffff",
                  borderRadius: 4,
                }}
              />
            )}
          </div>
        </div>
      ),
    });

    cols.push({
      accessorKey: "price",
      header: "Giá",
      size: 150,
      Cell: ({ row }: any) => {
        const raw = row.original.price || 0;
        const num = Number(String(raw).replace(/[^0-9.-]+/g, "")) || 0;
        const formatted = new Intl.NumberFormat("en-US").format(
          Math.round(num),
        );
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Text style={{ fontWeight: 600 }} key={`price-${row.original.id}`}>
              {formatted}đ
            </Text>
          </div>
        );
      },
    });
    cols.push({
      accessorKey: "sku",
      header: "Thao tác",
      size: 200,
      Cell: ({ row }: any) => {
        const skuVal = row.original.sku || "";
        return (
          <Group align="center">
            <Button
              variant="light"
              size="xs"
              color="blue"
              onClick={() => handleOpenFormChooseItemForRow(row.original.id)}
              leftSection={<IconCirclePlus size={16} />}
            >
              Chọn SP
            </Button>
          </Group>
        );
      },
    });

    return cols;
  }, [groups, updateSkuField, skus]);

  return (
    <div
      style={{
        padding: 24,
        background: "#fff",
        width: "100%",
      }}
    >
      <Group
        align="center"
        style={{
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          paddingBottom: 16,
          justifyContent: "space-between",
          display: "flex",
        }}
      >
        <Group align="center">
          <div
            style={{
              padding: 8,
              borderRadius: 8,
              background: "rgba(59,130,246,0.08)",
            }}
          >
            <IconPackage size={24} color="var(--mantine-color-blue-6)" />
          </div>
          <div>
            <Title order={2} style={{ letterSpacing: "-0.02em" }}>
              Quản lý biến thể sản phẩm
            </Title>
            <Text color="dimmed">
              Tạo và quản lý các phiên bản sản phẩm khác nhau
            </Text>
          </div>
        </Group>

        <Group>
          <Button
            variant="filled"
            color="blue"
            size="sm"
            loading={isSaving}
            leftSection={<IconSpacingVertical size={16} />}
            onClick={handleSaveAndPublish}
          >
            Lưu & Hiển thị
          </Button>
        </Group>
      </Group>

      {/* ── Thông tin nhóm ─────────────────────────────────────── */}
      <Card
        withBorder
        radius="md"
        mb="md"
        style={{ background: "transparent" }}
      >
        <Title order={4} mb="md">
          Thông tin nhóm
        </Title>
        <Group grow wrap="nowrap" gap="lg">
          <TextInput
            label="Tên nhóm"
            placeholder="Nhập tên nhóm"
            withAsterisk
            value={groupName}
            onChange={(e) => setGroupName(e.currentTarget.value)}
            onKeyDown={preventLeadingSpaceOnKey}
            onPaste={makePasteHandler(setGroupName)}
          />
        </Group>
        <Group grow wrap="nowrap" gap="lg">
          <Textarea
            label="Mô tả tóm tắt"
            placeholder="Nhập mô tả"
            value={groupDescription}
            onChange={(e) => setGroupDescription(e.currentTarget.value)}
            onKeyDown={preventLeadingSpaceOnKey}
            onPaste={makePasteHandler(setGroupDescription)}
          />
        </Group>
      </Card>

      {/* ── Thiết lập phân loại ────────────────────────────────── */}
      <Card withBorder radius="md" style={{ background: "transparent" }}>
        <Group
          align="center"
          justify="space-between"
          style={{ padding: 16, paddingBottom: 0 }}
        >
          <div>
            <div>
              <Title order={4}>Thiết lập phân loại</Title>
            </div>
          </div>
          {groups.length < 2 && (
            <Button variant="filled" color="blue" onClick={addGroup}>
              <IconPlus size={16} /> Thêm nhóm phân loại mới
            </Button>
          )}
        </Group>

        {/* Product list table (EUI) - show grouped products like original */}

        {hasVariants && (
          <Stack style={{ padding: 16 }}>
            {groups.map((group, gIdx) => (
              <Card
                key={group.id}
                withBorder
                radius="md"
                style={{
                  position: "relative",
                  padding: 12,
                  background: "#f7f7f79c",
                }}
              >
                <Button
                  variant="transparent"
                  size="md"
                  style={{ position: "absolute", top: 8, right: 8 }}
                  onClick={() => removeGroup(group.id)}
                >
                  <IconX size={20} color="#4d4d4dff" />
                </Button>

                <Stack>
                  <Text style={{ fontWeight: "bold" }} size="md">
                    Tên phân loại {gIdx + 1}
                  </Text>
                  <TextInput
                    placeholder="Ví dụ: Màu sắc, Dung lượng..."
                    value={group.name}
                    onChange={(e) => {
                      const newGroups = [...groups];
                      newGroups[gIdx].name = e.target.value;
                      setGroups(newGroups);
                    }}
                    style={{ maxWidth: 360 }}
                  />

                  <Text size="sm">Các tùy chọn</Text>
                  <Group wrap="wrap">
                    {group.options.map((option) => (
                      <Group
                        key={option.id}
                        style={{
                          alignItems: "center",
                          border: "1px solid rgba(0,0,0,0.08)",
                          borderRadius: 6,
                          padding: "4px 8px",
                        }}
                      >
                        <TextInput
                          value={option.value}
                          onChange={(e) =>
                            updateOption(group.id, option.id, e.target.value)
                          }
                          size="xs"
                          placeholder="Nhập"
                          style={{ width: 150 }}
                        />
                        <Button
                          variant="transparent"
                          size="sm"
                          onClick={() => removeOption(group.id, option.id)}
                        >
                          <IconTrash color="#868686ff" size={16} />
                        </Button>
                      </Group>
                    ))}
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() => addOption(group.id)}
                      leftSection={<IconPlus size={12} />}
                    >
                      Thêm tùy chọn
                    </Button>
                  </Group>
                </Stack>
              </Card>
            ))}
          </Stack>
        )}
      </Card>

      {hasVariants &&
        skus.length > 0 &&
        (((groups[0]?.name || "").trim() ||
          (groups[1]?.name || "").trim()) as any) && (
          <div style={{ marginTop: 16 }}>
            <Card withBorder radius="md" style={{ marginTop: 8 }}>
              <Group
                style={{ paddingLeft: 8, paddingTop: 16, paddingBottom: 16 }}
              >
                <div>
                  <div>
                    <Title order={4}>Danh sách phân loại hàng</Title>
                  </div>
                </div>
              </Group>
              <ScrollArea>
                <>
                  <style>{`.cfg-table th, .cfg-table td { border-right: 1px solid rgba(0, 0, 0, 0.18); } .cfg-table th:last-child, .cfg-table td:last-child { border-right: none !important; }`}</style>
                  <MantineReactTable
                    columns={columns}
                    data={tableDataWithSpan}
                    enablePagination={false}
                    enableColumnActions={false}
                    enableColumnFilters={false}
                    enableSorting={false}
                    initialState={{
                      density: "xs" as any,
                      columnOrder: ["group1", "group2"],
                    }}
                    mantineTableHeadCellProps={{
                      style: {
                        padding: "10px 15px",
                        background: "#f0f0f0ff",
                        borderTop: "1px solid rgba(0, 0, 0, 0.18)",
                      },
                    }}
                    mantineTableBodyCellProps={{
                      align: "center",
                      style: {
                        verticalAlign: "middle",
                        padding: "0px 15px",
                      },
                    }}
                    mantineTableProps={{
                      className: "cfg-table",
                      striped: false,
                      highlightOnHover: false,
                    }}
                  />
                </>
              </ScrollArea>
            </Card>
          </div>
        )}

      {!hasVariants && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 48,
            border: "2px dashed rgba(0,0,0,0.08)",
            borderRadius: 24,
            background: "rgba(248,250,252,0.6)",
            marginTop: 24,
          }}
        >
          <div
            style={{
              padding: 12,
              background: "var(--mantine-color-white)",
              border: "1px solid rgba(0,0,0,0.06)",
              borderRadius: 12,
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
              marginBottom: 16,
            }}
          >
            <IconLayoutGrid size={32} color="rgba(100,116,139,0.8)" />
          </div>
          <Title order={4}>Chưa có biến thể</Title>
          <Text
            color="dimmed"
            size="sm"
            style={{ maxWidth: 300, textAlign: "center", marginTop: 8 }}
          >
            Bật chế độ "Thiết lập phân loại" ở trên để bắt đầu thêm các thuộc
            tính cho sản phẩm của bạn.
          </Text>
        </div>
      )}
    </div>
  );
};

export default ConfigGroupProduct;

type ConfigGroupProductProps = {
  idGroup?: number;
  initialName?: string;
  initialDescription?: string;
  dataConfigGroupProduct?: TblConfigGroupProduct[] | [];
  dataConfigGroupAttribute?: TblConfigGroupAttribute[] | [];
  callApiGetData?: () => Promise<void>;
  onSaved?: () => Promise<void> | void;
};
